import { Mail } from 'lucide-react'
import { useMemo } from 'react'
import { useEmailQuery } from '../hooks/useEmailQuery'
import type { Donation } from '../types/donation'
import { formatDate } from '@/utils/timeZoneUtils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useIsMobile } from '@/hooks'

export function GmailMessageView({ donation }: { donation: Donation }) {
  const isMobile = useIsMobile()
  if (!donation.emailRaw) {
    return (
      <div className="text-center py-8">
        <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-500">No email data available</p>
      </div>
    )
  }

  const parseEmailContent = () => {
    try {
      const parsedData = JSON.parse(donation.emailRaw!)

      // Extract headers
      const headers = parsedData.payload?.headers || []
      const from =
        headers.find((h: any) => h.name === 'From')?.value ||
        donation.emailFrom ||
        'Unknown Sender'
      const subject =
        headers.find((h: any) => h.name === 'Subject')?.value ||
        donation.emailSubject ||
        'No Subject'
      const dateHeader = headers.find((h: any) => h.name === 'Date')?.value
      const date = dateHeader
        ? formatDate(new Date(dateHeader), 'DD/MM/YYYY hh:mm:ss A')
        : formatDate(
            new Date(donation.createdAt || Date.now()),
            'DD/MM/YYYY hh:mm:ss A',
          )

      // Base64 URL safe decoding function
      const decodeBase64UrlSafe = (str: string): string => {
        try {
          let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
          const padding = base64.length % 4
          if (padding) {
            base64 += '='.repeat(4 - padding)
          }
          return atob(base64)
        } catch (error) {
          console.error('Error decoding base64:', error)
          return ''
        }
      }

      // Recursively extract body content from nested multipart structure
      const extractBody = (payload: any): string => {
        if (payload.body?.data) {
          return decodeBase64UrlSafe(payload.body.data)
        }
        if (payload.parts && payload.parts.length > 0) {
          for (const part of payload.parts) {
            if (part.mimeType === 'text/html' && part.body?.data) {
              return decodeBase64UrlSafe(part.body.data)
            }
          }
          for (const part of payload.parts) {
            if (part.mimeType === 'text/plain' && part.body?.data) {
              return decodeBase64UrlSafe(part.body.data)
            }
          }
          for (const part of payload.parts) {
            const body = extractBody(part)
            if (body) return body
          }
        }
        return ''
      }

      const bodyContent =
        extractBody(parsedData.payload) || 'No content available'

      return { from, subject, date, bodyContent }
    } catch (error) {
      console.error('Error parsing email data:', error)
      return {
        from: donation.emailFrom || 'Unknown Sender',
        subject: donation.emailSubject || 'No Subject',
        date: formatDate(
          new Date(donation.createdAt || Date.now()),
          'DD/MM/YYYY hh:mm:ss A',
        ),
        bodyContent: 'Error parsing email content',
      }
    }
  }

  const { bodyContent } = parseEmailContent()

  // Sanitize HTML content for safe display
  const sanitizeHTML = (html: string): string => {
    let sanitized = html
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/<link[^>]*>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')

    if (isMobile) {
      sanitized = sanitized
        .replace(/width\s*=\s*["']?\d+["']?/gi, '')
        .replace(/style\s*=\s*["'][^"']*width\s*:\s*\d+px[^"']*["']/gi, '')
        .replace(/style\s*=\s*["'][^"']*min-width\s*:\s*\d+px[^"']*["']/gi, '')
        .replace(
          /padding-left\s*:\s*72px\s*;\s*padding-right\s*:\s*72px\s*;/gi,
          'padding-left:12px;padding-right:12px;',
        )
        .replace(
          /padding-right\s*:\s*72px\s*;\s*padding-left\s*:\s*72px\s*;/gi,
          'padding-left:12px;padding-right:12px;',
        )
        .replace(/<table[^>]*width\s*=\s*["']?\d+["']?[^>]*>/gi, (match) =>
          match.replace(/width\s*=\s*["']?\d+["']?/gi, 'width="100%"'),
        )
        .replace(
          /<table([^>]*)>/gi,
          '<table$1 style="width: 100%; max-width: 100%; table-layout: auto;">',
        )
        .replace(/<td[^>]*width\s*=\s*["']?\d+["']?[^>]*>/gi, (match) =>
          match.replace(/width\s*=\s*["']?\d+["']?/gi, ''),
        )
        .replace(/<th[^>]*width\s*=\s*["']?\d+["']?[^>]*>/gi, (match) =>
          match.replace(/width\s*=\s*["']?\d+["']?/gi, ''),
        )
        .replace(
          /<table([^>]*)>/gi,
          '<table$1 style="width: 100%; max-width: 100%; table-layout: auto; padding: 2px;">',
        )
        .replace(/<tbody([^>]*)>/gi, '<tbody$1 style="padding: 1px;">')
        .replace(/<tr([^>]*)>/gi, '<tr$1 style="padding: 1px;">')
        .replace(/<td([^>]*)>/gi, '<td$1 style="padding: 2px 4px;">')
        .replace(/<th([^>]*)>/gi, '<th$1 style="padding: 2px 4px;">')
    }

    return sanitized
  }

  const sanitizedBodyContent = useMemo(
    () =>
      bodyContent.includes('<')
        ? sanitizeHTML(bodyContent)
        : bodyContent.replace(/\n/g, '<br>').replace(/\r/g, ''),
    [bodyContent],
  )

  const {
    data: imageUrl,
    isLoading,
    isError,
  } = useEmailQuery(sanitizedBodyContent)

  return (
    <div className="gmail-view bg-white rounded-lg border overflow-hidden w-full min-h-[60vh] flex items-center justify-center">
      <ScrollArea className={isMobile ? 'h-fit' : 'h-[60vh]'}>
        {isLoading && (
          <div className="flex items-center justify-center w-[95vw] max-w-[850px] h-[65vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700 mx-auto mb-4"></div>
              <p>Generating email</p>
            </div>
          </div>
        )}
        {isError && (
          <div className="flex items-center justify-center h-[400px]">
            <p>Error generating document. Please try again.</p>
          </div>
        )}
        {imageUrl && (
          <div
            className={isMobile ? 'w-full h-full overflow-auto' : ''}
            style={isMobile ? { WebkitOverflowScrolling: 'touch' } : {}}
          >
            <img
              src={imageUrl}
              alt="Email Preview"
              className={
                isMobile ? 'w-full h-auto' : 'w-auto h-full max-h-full'
              }
              style={
                isMobile
                  ? { touchAction: 'none', userSelect: 'none' }
                  : { display: 'block' }
              }
              draggable={false}
            />
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
